/**
 * Blocks leaving the current screen when the profile form has unsaved edits.
 * Web only — native uses the sticky save button without a leave confirmation.
 * Covers header Back and browser back via `beforeRemove`.
 * Call the returned `allowLeave` before navigating away after a successful save.
 */
import { useNavigation } from "expo-router";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";

import { showAlert } from "@/lib/showAlert";
import { TEST_IDS } from "@/testIdDefinition";

type NavigationAction = {
    type: string;
    payload?: object;
    source?: string;
    target?: string;
};

export function useConfirmDiscardUnsavedChanges(isDirty: boolean) {
    const navigation = useNavigation();
    const { t } = useTranslation();
    const allowLeaveRef = useRef(false);
    const shouldConfirm = Platform.OS === "web" && isDirty;

    const allowLeave = useCallback(() => {
        allowLeaveRef.current = true;
    }, []);

    useEffect(() => {
        if (!shouldConfirm) {
            return;
        }

        const unsubscribe = navigation.addListener("beforeRemove", (event) => {
            if (allowLeaveRef.current) {
                return;
            }

            event.preventDefault();

            const action = event.data.action as NavigationAction;

            showAlert(
                t("profile.unsavedChangesTitle"),
                t("profile.unsavedChangesMessage"),
                [
                    {
                        text: t("profile.unsavedChangesKeepEditing"),
                        variant: "outline",
                    },
                    {
                        text: t("profile.unsavedChangesDiscard"),
                        variant: "destructive",
                        testID: TEST_IDS[
                            TEST_IDS.PROFILE_DISCARD_CHANGES_BUTTON
                        ],
                        onPress: () => {
                            allowLeaveRef.current = true;
                            navigation.dispatch(action);
                        },
                    },
                ]
            );
        });

        return unsubscribe;
    }, [navigation, shouldConfirm, t]);

    return allowLeave;
}
